import React from 'react';
import { ArrowLeft, Trash2, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DataDeletion = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  const supportEmail = "pankajacharyaorg@gmail.com";
  const emailSubject = encodeURIComponent("User Data Deletion Request");
  const emailBody = encodeURIComponent(
    `Hello JustLabs Support,\n\n` +
    `I would like to request the permanent deletion of all personal data and records associated with my use of your applications and services.\n\n` +
    `Please find my account/request details below:\n` +
    `- Full Name: \n` +
    `- Registered Email Address: \n` +
    `- Application Name(s) Used (e.g. JustSolar, JustPDF, JustPrint, JustSaver, JustConverter): \n` +
    `- Device Identifier or Payment Transaction ID (if applicable): \n` +
    `- Specific Deletion Notes: \n\n` +
    `Thank you,\n`
  );

  return (
    <div className="privacy-policy">
      <div className="privacy-policy-container">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={20} />
          Back to Home
        </button>

        <div className="privacy-policy-content">
          <div className="policy-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(220, 38, 38, 0.1)', color: '#dc2626', padding: '4px 12px', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 600, marginBottom: '1rem' }}>
            <Trash2 size={16} />
            User Privacy & Data Safety
          </div>

          <h1>User Data Deletion Request</h1>
          <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2>1. Our Commitment to Your Data Privacy</h2>
            <p>
              At <strong>JustLabs</strong> (operated by <strong>Pankaj Acharya</strong>), we are committed to upholding the highest standards of data safety and privacy, including full compliance with Google Play Developer Policies and global data protection regulations.
            </p>
            <p>
              We believe every user has the absolute right to know what data is collected and to have their data permanently deleted upon request.
            </p>
          </section>

          <section>
            <h2>2. Covered Applications</h2>
            <p>
              This Data Deletion policy and mechanism applies to all software applications and services published by JustLabs:
            </p>
            <ul>
              <li><strong>JustSolar</strong> — Solar EPC Calculator, Sizing, Proposal & BOM Generator</li>
              <li><strong>JustPDF</strong> (Image to PDF - JustPDF)</li>
              <li><strong>JustPrint</strong> (JustPrint - Printable Notes)</li>
              <li><strong>JustSaver</strong> (Status Saver - JustSaver)</li>
              <li><strong>JustConverter</strong> (JPG to PNG - JustConverter)</li>
              <li><strong>Website Inquiries & Direct Purchases</strong> via justlabs.in</li>
            </ul>
          </section>

          <section>
            <h2>3. Types of Data & Deletion Scope</h2>
            <p>
              Most of our utility applications (such as JustPDF, JustConverter, JustSaver, and JustPrint) run <strong>offline-first</strong>, processing your photos, documents, and notes strictly on your local device.
            </p>
            <p>Upon receiving a data deletion request, we will permanently delete:</p>
            <ul>
              <li>Your customer inquiry logs and support tickets.</li>
              <li>Commercial license records, invoice contact info, and activation associations (e.g. for JustSolar).</li>
              <li>Any name, email address, or phone number stored in our active communication registers.</li>
              <li>Any diagnostic or voluntary feedback reports you have transmitted to us.</li>
            </ul>
          </section>

          <section>
            <h2>4. How to Request Data Deletion</h2>
            <p>
              If you want your data or account records permanently erased, you can request it immediately by emailing us at <strong>pankajacharyaorg@gmail.com</strong>.
            </p>

            <div className="data-deletion-card">
              <div className="data-deletion-header">
                <Trash2 size={24} color="#dc2626" />
                <h3>Step-by-Step Deletion Request</h3>
              </div>
              <p>
                Click the button below to launch an email with pre-filled details, or compose an email from your personal client:
              </p>
              
              <div style={{ margin: '1rem 0' }}>
                <a 
                  href={`mailto:${supportEmail}?subject=${emailSubject}&body=${emailBody}`}
                  className="data-deletion-btn"
                >
                  <Send size={18} />
                  Send Email to Request Data Deletion
                </a>
              </div>

              <div className="data-deletion-email-direct">
                <strong>Recipient:</strong> <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
              </div>

              <p style={{ marginTop: '1rem', fontSize: '0.95rem' }}>
                Please provide the following in your request:
              </p>
              <ul>
                <li><strong>Your Full Name & Registered Email</strong></li>
                <li><strong>Application Name(s)</strong> you have used</li>
                <li><strong>Device Identifier or Payment ID</strong> (if you previously purchased a license key)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2>5. Processing Timeline & Confirmation</h2>
            <ul>
              <li><strong>Verification:</strong> We acknowledge receipt of your data deletion request within <strong>24 business hours</strong>.</li>
              <li><strong>Execution:</strong> All associated records will be permanently purged from our databases and operational logs within <strong>48 to 72 business hours</strong>.</li>
              <li><strong>Confirmation:</strong> You will receive a final confirmation email confirming that your data has been permanently deleted.</li>
            </ul>
          </section>

          <section>
            <h2>6. Contact & Grievance Information</h2>
            <div className="contact-info">
              <p>For any questions regarding this policy or immediate assistance, reach out directly to:</p>
              <p><strong>Proprietor:</strong> Pankaj Acharya</p>
              <p><strong>Entity:</strong> JustLabs</p>
              <p><strong>Direct Support Email:</strong> <a href={`mailto:${supportEmail}`}>{supportEmail}</a></p>
              <p><strong>Operating Jurisdiction:</strong> Ajmer, Rajasthan, India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
